import { useState, useEffect, useContext, useRef } from 'react';
import { FreightCardGridView } from './FreightCardGridView';
import {
  ETransportFreightPlutoDto,
  GetAdminFreightsParams,
  GetAdminTargetsParams,
  GetFreightsParams,
  MinimalCustomer,
  MinimalTarget,
  PlutoCustomerDto,
  PlutoFreightResponseDto,
} from '@/generated/model';
import {
  getAdminFreights,
  getAdminTargets,
  getETransportCustomers,
  getFreights,
  getTargets1,
  useGetSubscriptions,
} from '@/generated/client/etransport/etransport';
import { useGetUserCustomers } from '@/generated/client/users/users';
import { TargetFreightsDetail } from './TargetFreightsDetails';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { TargetFreightsSubmitForm } from './TargetFreightsSubmitForm';
import { FreightSubmitForm } from './FreightSubmitForm';
import { Dialog } from '@mui/material';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { ctxUser } from '@/contexts/UserContext';
import { SubscriptionsPanel } from './subscriptions/SubscriptionsPanel';

import './etransport.css';

export default function EtransportPopup({ close }: { close: () => void }) {
  // const { t } = useTranslation();
  const userCtx = useContext(ctxUser);

  const [freights, setFreights] = useState<PlutoFreightResponseDto | undefined>(undefined);
  const [targets, setTargets] = useState<MinimalTarget[] | undefined>(undefined);
  const [customers, setCustomers] = useState<PlutoCustomerDto[] | undefined>(undefined);
  const [customer, setCustomer] = useState<PlutoCustomerDto | undefined>(undefined);
  const [nonAdminSubscriptionCustomerId, setNonAdminSubscriptionCustomerId] = useState<number | null>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newChangesAdded, setNewChangesAdded] = useState<FreightDataChange | null>(null);
  const [openTargetFreightSubmitForm, setOpenTargetFreightSubmitForm] = useState<boolean>(false);
  const [openTargetFreightDetails, setOpenTargetFreightDetails] = useState<boolean>(false);
  const [openFreightSubmitForm, setOpenFreightSubmitForm] = useState<boolean>(false);
  const [openSubscriptions, setOpenSubscriptions] = useState<boolean>(false);
  const [currentFreight, setCurrentFreight] = useState<ETransportFreightPlutoDto | undefined>(undefined);
  const [searchTarget, setSearchTarget] = useState<string | undefined>(undefined);
  const [isClosingDetails, setIsClosingDetails] = useState<boolean>(false);
  const [isClosingSubmitForm, setIsClosingSubmitForm] = useState<boolean>(false);
  const subscriptionButtonRef = useRef<HTMLButtonElement>(null);

  const isAdmin = userCtx?.user.isSuperAdmin ?? false;
  const userCustomersQuery = useGetUserCustomers({ query: { enabled: !isAdmin } });
  const subscriptionCustomers: MinimalCustomer[] = isAdmin
    ? (customers ?? []).flatMap((item) =>
        item.id === undefined ? [] : [{ id: item.id, name: item.name ?? String(item.id) }]
      )
    : userCustomersQuery.isError || userCustomersQuery.isLoading
      ? []
      : (userCustomersQuery.data?.data ?? []);
  const requestedSubscriptionCustomerId = isAdmin
    ? customer?.id
    : nonAdminSubscriptionCustomerId === undefined
      ? subscriptionCustomers[0]?.id
      : (nonAdminSubscriptionCustomerId ?? undefined);
  const subscriptionBannerCustomerId = subscriptionCustomers.find(
    (item) => item.id === requestedSubscriptionCustomerId
  )?.id;
  const isLoadingSubscriptionCustomers = isAdmin ? isLoading && customers === undefined : userCustomersQuery.isLoading;
  const subscriptionCustomersLoadError = isAdmin ? !isLoading && customers === undefined : userCustomersQuery.isError;
  const subscriptionBannerQuery = useGetSubscriptions(
    { customerId: subscriptionBannerCustomerId ?? 0 },
    { query: { enabled: subscriptionBannerCustomerId !== undefined } }
  );
  const showNoActiveSubscriptionBanner =
    subscriptionBannerCustomerId !== undefined &&
    !subscriptionBannerQuery.isLoading &&
    !subscriptionBannerQuery.isError &&
    subscriptionBannerQuery.data !== undefined &&
    !(subscriptionBannerQuery.data.data ?? []).some((subscription) => subscription.revokedAtMicros == null);

  function updateCurrentFreight(freights: PlutoFreightResponseDto) {
    const groups = [freights.active, freights.inactive, freights.future];
    for (const group of groups) {
      const match = group?.freights?.find((freight) => freight.uit === currentFreight?.uit);
      if (match) {
        setCurrentFreight(match);
        return;
      }
    }
    setCurrentFreight(undefined);
  }

  async function fetchData(updateFreight: boolean) {
    setIsLoading(true);
    try {
      if (isAdmin) {
        const customersResult = await getETransportCustomers();
        const customersList = customersResult?.data.customers ?? [];
        setCustomers(customersList);
        let defaultCustomer: PlutoCustomerDto;
        if (!customer) {
          defaultCustomer = customersList[0];
          setCustomer((prev) => prev ?? defaultCustomer);
        } else {
          defaultCustomer = customer;
        }

        const customerId = defaultCustomer?.id ? String(defaultCustomer.id) : '';
        const [freightsResult, targetsResult] = await Promise.all([
          getAdminFreights({ filter: searchTarget, customerId: customerId } satisfies GetAdminFreightsParams),
          getAdminTargets({ customerId: customerId } satisfies GetAdminTargetsParams),
        ]);
        const freights: PlutoFreightResponseDto = freightsResult.data;
        setTargets(targetsResult.data);
        setFreights(freights);
        if (updateFreight) updateCurrentFreight(freights);
      } else {
        const [freightsResult, targetsResult] = await Promise.all([
          getFreights({ filter: searchTarget } satisfies GetFreightsParams),
          getTargets1(),
        ]);
        const freights: PlutoFreightResponseDto = freightsResult.data;
        setTargets(targetsResult.data);
        setFreights(freights);
        if (updateFreight) updateCurrentFreight(freights);
      }
    } catch (err) {
      setFreights(undefined);
      setTargets(undefined);
      setCustomers(undefined);
      if (updateFreight) setCurrentFreight(undefined);
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTarget === undefined && customer == undefined) return;
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTarget, customer]);

  async function handleRefetch(): Promise<void> {
    await fetchData(true);
  }

  function handleSubscriptionCustomerChange(customerId: number | undefined) {
    if (isAdmin) {
      setCustomer(customers?.find((item) => item.id === customerId));
    } else {
      setNonAdminSubscriptionCustomerId(customerId ?? null);
    }
  }

  function handleRetrySubscriptionCustomers() {
    if (isAdmin) {
      void handleRefetch();
    } else {
      void userCustomersQuery.refetch();
    }
  }

  function handleCloseSubscriptions() {
    setOpenSubscriptions(false);
    requestAnimationFrame(() => subscriptionButtonRef.current?.focus());
  }

  return isPhoneOrHighScreen() ? (
    <Dialog fullScreen open={true} onClose={close} className="popup-base-whole" hideBackdrop={true}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base phone"
          data-qa-element-id="etr_freight_card_grid"
        >
          <FreightCardGridView
            targets={targets}
            freights={freights}
            customer={customer}
            customers={customers}
            isLoading={isLoading}
            searchTarget={searchTarget}
            currentFreight={currentFreight}
            subscriptionButtonRef={subscriptionButtonRef}
            showNoActiveSubscriptionBanner={showNoActiveSubscriptionBanner}
            onRefetch={handleRefetch}
            setCustomer={setCustomer}
            setNewChanges={setNewChangesAdded}
            setSearchTarget={setSearchTarget}
            setCurrentFreight={setCurrentFreight}
            setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            setOpenSubscriptions={setOpenSubscriptions}
            setOpenTargetFreightDetails={setOpenTargetFreightDetails}
            setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
          />
        </div>

        {openSubscriptions && (
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone"
            data-qa-element-id="etr_subscriptions_panel"
          >
            <SubscriptionsPanel
              customers={subscriptionCustomers}
              selectedCustomerId={requestedSubscriptionCustomerId}
              isLoadingCustomers={isLoadingSubscriptionCustomers}
              customersLoadError={subscriptionCustomersLoadError}
              onCustomerChange={handleSubscriptionCustomerChange}
              onRetryCustomers={handleRetrySubscriptionCustomers}
              onClose={handleCloseSubscriptions}
              onFreightsChanged={handleRefetch}
            />
          </div>
        )}

        {(openTargetFreightDetails || isClosingDetails) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_detail_panel"
          >
            {openTargetFreightSubmitForm ? (
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            ) : (
              <TargetFreightsDetail
                targets={targets}
                freight={currentFreight}
                newChange={newChangesAdded}
                openTargetFreightSubmitForm={openTargetFreightSubmitForm}
                onRefetch={handleRefetch}
                setNewChanges={setNewChangesAdded}
                setCurrentFreight={setCurrentFreight}
                setIsClosingDetails={setIsClosingDetails}
                setOpenTargetFreightDetails={setOpenTargetFreightDetails}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            )}
          </div>
        )}

        {(openFreightSubmitForm || isClosingSubmitForm) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        )}
      </div>
    </Dialog>
  ) : (
    <div style={{ position: 'relative', width: '500px' }}>
      <div
        className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base"
        data-qa-element-id="etr_freight_card_grid"
      >
        <FreightCardGridView
          targets={targets}
          freights={freights}
          customer={customer}
          customers={customers}
          isLoading={isLoading}
          searchTarget={searchTarget}
          currentFreight={currentFreight}
          subscriptionButtonRef={subscriptionButtonRef}
          showNoActiveSubscriptionBanner={showNoActiveSubscriptionBanner}
          onRefetch={handleRefetch}
          setCustomer={setCustomer}
          setNewChanges={setNewChangesAdded}
          setSearchTarget={setSearchTarget}
          setCurrentFreight={setCurrentFreight}
          setOpenFreightSubmitForm={setOpenFreightSubmitForm}
          setOpenSubscriptions={setOpenSubscriptions}
          setOpenTargetFreightDetails={setOpenTargetFreightDetails}
          setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
        />
      </div>
      {openSubscriptions && (
        <div className="etransport-overlay-container">
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_subscriptions_panel"
          >
            <SubscriptionsPanel
              customers={subscriptionCustomers}
              selectedCustomerId={requestedSubscriptionCustomerId}
              isLoadingCustomers={isLoadingSubscriptionCustomers}
              customersLoadError={subscriptionCustomersLoadError}
              onCustomerChange={handleSubscriptionCustomerChange}
              onRetryCustomers={handleRetrySubscriptionCustomers}
              onClose={handleCloseSubscriptions}
              onFreightsChanged={handleRefetch}
            />
          </div>
        </div>
      )}
      {(openTargetFreightDetails || isClosingDetails) && (
        <div
          className={`etransport-overlay-container ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_detail_panel"
          >
            <TargetFreightsDetail
              targets={targets}
              freight={currentFreight}
              newChange={newChangesAdded}
              openTargetFreightSubmitForm={openTargetFreightSubmitForm}
              onRefetch={handleRefetch}
              setNewChanges={setNewChangesAdded}
              setCurrentFreight={setCurrentFreight}
              setIsClosingDetails={setIsClosingDetails}
              setOpenTargetFreightDetails={setOpenTargetFreightDetails}
              setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
            />
          </div>
          {openTargetFreightSubmitForm && (
            <div className="etransport-freight-panel" data-qa-element-id="etr_freight_target_submit_panel">
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            </div>
          )}
        </div>
      )}
      {(openFreightSubmitForm || isClosingSubmitForm) && (
        <div
          className={`etransport-overlay-container ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
